import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { useUI } from '../../contexts/UIContext';
import QueryStatsCards from '../../components/admin/QueryStatsCards';
import { CurrencyDollarIcon, ChartBarIcon } from '@heroicons/react/24/outline';

/**
 * Cost Monitoring Page
 *
 * Admin page for monitoring Snowflake credit consumption by Sparvi.
 * Shows cost breakdowns, trends, and expensive queries.
 */
const CostMonitoringPage = () => {
  const { showNotification } = useUI();

  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30); // days
  const [selectedConnection, setSelectedConnection] = useState('');
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    loadCostData();
  }, [dateRange, selectedConnection]);

  const loadConnections = async () => {
    try {
      const response = await apiRequest('/connections', { skipThrottle: true });
      if (response?.data) {
        // Filter for Snowflake connections only
        const snowflakeConns = response.data.filter(
          (conn) => conn.connection_type === 'snowflake'
        );
        setConnections(snowflakeConns);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const loadCostData = async () => {
    try {
      setLoading(true);

      const params = {};

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      params.start_date = startDate.toISOString();
      params.end_date = endDate.toISOString();

      if (selectedConnection) {
        params.connection_id = selectedConnection;
      }

      const response = await apiRequest('/admin/query-logs/cost', {
        params,
        skipThrottle: true
      });

      if (response?.data) {
        setCostData(response.data);
      } else {
        setCostData(null);
      }
    } catch (error) {
      console.error('Error loading cost data:', error);
      showNotification('Failed to load cost data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    setDateRange(days);
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading cost data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Cost Monitoring</h2>
        <p className="text-sm text-gray-600 mt-1">
          Monitor Snowflake credit consumption by Sparvi automations.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => handleDateRangeChange(days)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    dateRange === days
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>

          {/* Connection Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Snowflake Connection
            </label>
            <select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Connections</option>
              {connections.map((conn) => (
                <option key={conn.id} value={conn.id}>
                  {conn.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Cost Summary Cards */}
      {costData && <QueryStatsCards stats={costData} loading={false} variant="cost" />}

      {/* No Data Message */}
      {costData && costData.queries_with_cost === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <CurrencyDollarIcon className="h-12 w-12 text-blue-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">No cost data available</p>
          <p className="text-sm text-gray-600 mt-1">
            Cost tracking requires Snowflake queries with credit information. This data may not be
            available for all query types or connection configurations.
          </p>
        </div>
      )}

      {/* Cost Breakdown Sections */}
      {costData && costData.queries_with_cost > 0 && (
        <>
          {/* Daily Trend Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Daily Credit Consumption
            </h3>
            <div className="space-y-2">
              {costData.daily_trend && costData.daily_trend.length > 0 ? (
                costData.daily_trend.map((day) => (
                  <div key={day.date} className="flex items-center">
                    <div className="w-24 text-sm text-gray-600">{formatDate(day.date)}</div>
                    <div className="flex-1">
                      <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-green-500 h-full flex items-center justify-end pr-2"
                          style={{
                            width: `${
                              (day.credits / Math.max(...costData.daily_trend.map((d) => d.credits))) *
                              100
                            }%`
                          }}
                        >
                          <span className="text-xs text-white font-medium">
                            {day.credits.toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No daily data available</p>
              )}
            </div>
          </div>

          {/* Cost by Connection */}
          {costData.by_connection && Object.keys(costData.by_connection).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-gray-500" />
                Cost by Connection
              </h3>
              <div className="space-y-3">
                {Object.entries(costData.by_connection)
                  .sort((a, b) => b[1] - a[1])
                  .map(([connection, credits]) => (
                    <div key={connection} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{connection}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-900">
                          {credits.toFixed(4)} credits
                        </span>
                        <span className="text-sm text-gray-500">
                          ${(credits * 2.5).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Cost by Automation Type */}
          {costData.by_automation_type && Object.keys(costData.by_automation_type).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 text-gray-500" />
                Cost by Automation Type
              </h3>
              <div className="space-y-3">
                {Object.entries(costData.by_automation_type)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, credits]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {type.replace(/_/g, ' ')}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-900">
                          {credits.toFixed(4)} credits
                        </span>
                        <span className="text-sm text-gray-500">
                          ${(credits * 2.5).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Top Expensive Queries */}
          {costData.top_expensive_queries && costData.top_expensive_queries.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Top 10 Most Expensive Queries
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Executed At
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Connection
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Automation Type
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Warehouse
                      </th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Credits Used
                      </th>
                      <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                        Est. Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {costData.top_expensive_queries.map((query) => (
                      <tr key={query.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(query.executed_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {query.connection_name || 'Unknown'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600">
                          {query.automation_type || 'manual'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600">
                          {query.warehouse_name || 'N/A'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 text-right font-medium">
                          {query.credits_used?.toFixed(6) || '0'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600 text-right">
                          ${((query.credits_used || 0) * 2.5).toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Note about cost estimation */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> Cost estimates are based on a default rate of $2.50 per Snowflake
          credit. Actual costs may vary based on your Snowflake pricing agreement. Credits shown
          represent consumption by Sparvi automations only.
        </p>
      </div>
    </div>
  );
};

export default CostMonitoringPage;
